/* ============================================================
   SABDIA CONSTRUCTIONS — MAIN JS (v4)

   Runs under Astro's ClientRouter. On a client-side navigation the
   document survives and <body> is replaced, which splits everything
   here into two scopes:

     · ONE-TIME — bound to document/window. This file is fetched once
       and never re-executed (Astro dedupes by src), so anything at
       module top level registers exactly once and lives for the whole
       session. Such handlers must look elements up at event time and
       never close over them, or they hold references to the previous
       page's DOM.

     · PER PAGE — bound to elements inside <body>. Re-run on every
       astro:page-load, which also fires on the very first load, so it
       is the single entry point rather than a special case.

   Anything with a lifetime — intervals, observers, rAF loops — is
   tracked and torn down before the next page wires up. Miss one and
   you get an extra copy per navigation: two slideshow timers fighting
   over the same hero, three scroll handlers, and so on.
   ============================================================ */

/* Belt and braces. Astro is expected to run this file once and skip it on
   subsequent swaps, but that is a behaviour of the router rather than a
   guarantee we control. If it ever did re-execute, every document-level
   listener below would gain a second copy — including the form handler,
   which would submit each enquiry twice. The IIFE closes at end of file. */
(function () {
if (window.__sabdiaMainInit) return;
window.__sabdiaMainInit = true;

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const finePointer = window.matchMedia('(pointer: fine)').matches;

/* ── LIFETIME TRACKING ─────────────────────────────────────── */
let pageIntervals = [];
let pageObservers = [];
let pageScroll = [];   // per-page scroll work, driven by the one listener below

const onInterval = (fn, ms) => { pageIntervals.push(setInterval(fn, ms)); };
const onObserve = (obs) => { pageObservers.push(obs); return obs; };
const onScroll = (fn) => { pageScroll.push(fn); };

function teardown() {
  pageIntervals.forEach(clearInterval);
  pageObservers.forEach((o) => o.disconnect());
  pageIntervals = [];
  pageObservers = [];
  pageScroll = [];
}

/* ============================================================
   ONE-TIME — document & window scope
   ============================================================ */

/* Astro replaces <html>'s attributes with the incoming document's on
   swap, and the incoming markup is server-rendered — so the classes
   added by the head script are lost on every client-side navigation.
   `js` gates the whole reveal system (without it .reveal never hides,
   so nothing animates in), and by definition the opening loader has
   been seen by the time any navigation happens. */
document.addEventListener('astro:after-swap', () => {
  document.documentElement.classList.add('js', 'no-loader');
});

/* Navigation feedback. The gap between a click and the new page's HTML
   arriving is entirely invisible otherwise — no spinner, no page unload,
   nothing — so a slow render reads as an interface that ignored you.
   Driven from the preparation events rather than the swap, because it is
   precisely the fetch that needs covering. Classes live on <html>, which
   survives the swap; the bar itself is replaced with the rest of the body,
   so `nav-done` is cleared on the next tick ready for the next navigation. */
document.addEventListener('astro:before-preparation', () => {
  document.documentElement.classList.remove('nav-done');
  document.documentElement.classList.add('is-navigating');
});
document.addEventListener('astro:after-preparation', () => {
  document.documentElement.classList.remove('is-navigating');
  document.documentElement.classList.add('nav-done');
  setTimeout(() => document.documentElement.classList.remove('nav-done'), 600);
});

/* ── NAVIGATION VEIL — black SABDIA frame over every page change ──
   Owner request: each navigation shows the wordmark while the next page
   renders behind it. Raised the instant the fetch starts; on page-load
   it is held to a minimum on-screen time so it reads as a deliberate
   frame rather than a flash. The veil element is replaced with each
   body — the class lives on <html>, which survives the swap. Reduced
   motion skips the artificial hold but keeps the covering itself. */
const VEIL_MIN_MS = 1400;
let veilShownAt = 0;
let veilNav = 0; // navigation token — a newer navigation invalidates pending hides
document.addEventListener('astro:before-preparation', () => {
  veilNav++;
  veilShownAt = Date.now();
  document.documentElement.classList.add('nav-veil');
});
/* The swap resets <html> attributes to the incoming page's, which wipes
   the class raised above. after-swap runs synchronously inside the swap —
   re-asserting here means no frame is ever painted without the veil. */
document.addEventListener('astro:after-swap', () => {
  if (veilShownAt) document.documentElement.classList.add('nav-veil');
});
document.addEventListener('astro:page-load', () => {
  if (!veilShownAt) return; // initial full load — the homepage intro owns that moment
  const nav = veilNav;
  const hold = reduceMotion ? 0 : Math.max(0, VEIL_MIN_MS - (Date.now() - veilShownAt));
  /* Hold the veil until the incoming page's header image has painted, so
     the reveal is a composed page, never a bare header band waiting on a
     cold image render. Capped — a missing or dead hero can't lock the
     door — and the minimum hold still applies to fast, cached arrivals. */
  const hero = document.querySelector('.page-hero img, .prop-hero img, .hero img, .h-slides img');
  const heroReady = new Promise((done) => {
    if (!hero || hero.complete) return done();
    hero.addEventListener('load', done, { once: true });
    hero.addEventListener('error', done, { once: true });
    setTimeout(done, reduceMotion ? 900 : 2800);
  });
  const minHold = new Promise((done) => setTimeout(done, hold));
  Promise.all([minHold, heroReady]).then(() => {
    if (nav !== veilNav) return; // a newer navigation owns the veil now
    veilShownAt = 0;
    document.documentElement.classList.remove('nav-veil');
  });
});

/* A navigation cancels any open overlay, and body scroll-lock is set
   on the old body — but Astro carries inline style across the swap,
   so a lightbox left open would strand the next page unscrollable. */
document.addEventListener('astro:before-swap', () => {
  teardown();
  document.body.style.overflow = '';
});

// ── KEYBOARD VS MOUSE — restore real cursor on Tab ──────────
document.addEventListener('keydown', (e) => {
  if (e.key === 'Tab') {
    document.body.classList.add('kb-nav');
    document.documentElement.classList.remove('cursor-fx');
  }
});
document.addEventListener('mousedown', () => document.body.classList.remove('kb-nav'));

/* The moment a backgrounded tab is looked at, re-run the reveal sweep. A
   browser that suspended IntersectionObserver while hidden may have left
   elements unrevealed, and with .reveal-wipe that reads as missing content.
   Document-scoped, so registered once and routed through whatever the
   current page's sweep is. */
let revealSweep = null;
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible' && revealSweep) revealSweep();
});

// Escape closes the mobile menu from anywhere on the page, so it has to be
// document-scoped. Registered once and routed through mobNavApi, which
// initPage rebuilds — binding it to the button instead would only work
// while the button itself held focus.
let mobNavApi = null;
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && mobNavApi && mobNavApi.isOpen()) mobNavApi.close();
});

// ── CURSOR (cursor:fine devices) ────────────────────────────
// The elements live in Nav, so they are replaced on every navigation.
// The tracking loop is registered once and reads whatever the current
// page's elements are, via refs that initPage refreshes.
let curDot = null;
let curRing = null;
if (finePointer && !reduceMotion) {
  let mx = 0, my = 0, rx = 0, ry = 0;
  // Hide the system cursor only after the custom one is provably
  // following the mouse — never before, so a failure here can't leave
  // the visitor without a pointer.
  document.addEventListener('mousemove', (e) => {
    mx = e.clientX; my = e.clientY;
    if (!curDot) return;
    curDot.style.left = mx + 'px';
    curDot.style.top = my + 'px';
    document.documentElement.classList.add('cursor-fx');
  });
  (function animate() {
    if (curRing) {
      rx += (mx - rx) * 0.13; ry += (my - ry) * 0.13;
      curRing.style.left = rx + 'px';
      curRing.style.top = ry + 'px';
    }
    requestAnimationFrame(animate);
  })();
}

// ── SCROLL DRIVER ───────────────────────────────────────────
// One listener, one rAF, for every scroll-linked effect on the page.
// Previously three independent listeners each ran their own ticking
// flag, so a single scroll could schedule three frames of work.
(() => {
  let ticking = false;
  const frame = () => {
    ticking = false;
    for (const fn of pageScroll) fn();
  };
  const request = () => { if (!ticking) { ticking = true; requestAnimationFrame(frame); } };
  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', request, { passive: true });
})();

// ── SMOOTH SCROLL (delegated) ───────────────────────────────
document.addEventListener('click', (e) => {
  const a = e.target.closest && e.target.closest('a[href^="#"]');
  if (!a) return;
  const href = a.getAttribute('href');
  if (href === '#' || href.length < 2) return;
  const target = document.querySelector(href);
  if (!target) return;
  e.preventDefault();
  window.scrollTo({
    top: target.getBoundingClientRect().top + window.scrollY - 80,
    behavior: reduceMotion ? 'auto' : 'smooth'
  });
});

// ── CONTACT FORMS (delegated) ──────────────────────────────
// Two backends, one behaviour: the visitor never leaves the page.
//  • Shopify mode: POST to Shopify's /contact in the background (it emails
//    the store), then swap the form for the thank-you panel. Shopify's
//    spam challenge, if it ever fires, falls back to a normal submit.
//  • Sabdia API mode: POST to /api/contact as before.
// In Shopify mode the enquiry is also mirrored to the API (Customers,
// leads inbox) when Theme settings › Site plumbing has an endpoint.
/* Theme settings › Messages (17 Sep 2026: every word on the site is editable
   in Customize). WORD('sending', 'Sending…') is the editor's word, or
   today's word when the box is empty or the page has no SabdiaForms. */
/* The contact address never sits whole in the page code (21 Sep 2026):
   the layout hands it over in two halves and a message says [email] where
   it belongs. */
const SITE_MAIL = () => { const w = (window.SabdiaForms && window.SabdiaForms.words) || {}; return (w.mailU && w.mailD) ? w.mailU + '@' + w.mailD : ''; };
const WORD = (key, fallback) => { const w = window.SabdiaForms && window.SabdiaForms.words; const s = (w && typeof w[key] === 'string' && w[key].trim()) ? w[key] : fallback; return (typeof s === 'string' && s.indexOf('[email]') > -1) ? s.split('[email]').join(SITE_MAIL() || 'us') : s; };
const THANKS_HTML = (h, t) => '<div class="form-thanks" id="formThanks" role="status" tabindex="-1"><div class="form-thanks-k" aria-hidden="true">&#10003;</div><h3 class="form-thanks-h">' + h + '</h3><p class="form-thanks-p">' + t + '</p></div>';
function showThanks(form, heading, text) {
  const cfg = (window.SabdiaForms && window.SabdiaForms.thanks) || {};
  const wrap = document.createElement('div');
  wrap.innerHTML = THANKS_HTML(heading || cfg.heading || 'Thank you', text || cfg.text || 'We will be in touch within a business day.');
  const panel = wrap.firstChild;
  form.replaceWith(panel);
  panel.classList.add('vis');
  /* Naomi, 14 Sep 2026: no jumping about after a send. The panel takes the
     form's place; the page only moves if the panel is not fully in view,
     and then just far enough. */
  setTimeout(() => { keepInView(panel); panel.focus({ preventScroll: true }); }, 60);
}
function keepInView(el, behavior) {
  const r = el.getBoundingClientRect(), navH = 90;
  if (r.top >= navH && r.bottom <= window.innerHeight) return;
  el.scrollIntoView({ behavior: behavior || 'smooth', block: r.height > window.innerHeight - navH ? 'start' : 'nearest' });
}
function thanksText(form) {
  /* A tailored thank-you for the chosen enquiry type wins (Customize: "Option | text"). */
  const sel = form.querySelector('select[name="contact[Enquiry type]"], select[name="contact[Interest]"]');
  if (sel && sel.value) {
    const special = Array.from(form.querySelectorAll('[data-thanks-for]')).find((s) => s.getAttribute('data-thanks-for') === sel.value);
    if (special && special.textContent.trim()) return special.textContent.trim();
  }
  /* Form thank-you blocks (Customize › Sabdia replies › Form thank-you) win next. */
  const v0 = (n) => { const e = form.querySelector('[name="' + n + '"]'); return e ? String(e.value || '') : ''; };
  const askedText = ((sel ? sel.value : '') + ' ' + v0('contact[body]') + ' ' + v0('contact[Agency]')).toLowerCase();
  const frEl = document.querySelector('[data-form-replies]');
  let central = [];
  try { const arr = JSON.parse(frEl ? frEl.textContent : '[]'); for (let i = 0; i + 1 < arr.length; i += 2) central.push({ k: arr[i] || '', q: '', a: arr[i + 1] || '' }); } catch (e) { central = []; }
  if (central.length) {
    const STOPF = new Set('you your can could the and for are with this that what how does have has any our its get from into will would like want need please hello hey thanks tell more about there here they them then than some just also very really when where which who why yes not but all one two new house home homes property properties sabdia'.split(' '));
    const wordsF = (t) => String(t || '').toLowerCase().replace(/[^a-z0-9$\s]/g, ' ').split(/\s+/).filter((w) => w.length > 2 && !STOPF.has(w));
    const hitF = (bag, w) => bag.some((b) => b === w || (w.length > 4 && b.length > 4 && (b.indexOf(w.slice(0, 5)) === 0 || w.indexOf(b.slice(0, 5)) === 0)));
    const ws = wordsF(askedText); let best = null, bestScore = 0;
    central.forEach((item) => { const kw = wordsF(item.k), qw = wordsF(item.q); let sc = 0; ws.forEach((w) => { if (hitF(kw, w)) sc += 2; else if (hitF(qw, w)) sc += 1; }); if (sc > bestScore) { bestScore = sc; best = item; } });
    if (best && bestScore >= 2) return best.a;
  }
  /* Otherwise pick Sabdia's reply by what the person asked for: the chosen
     option plus their message (a "General" enquiry about a renovation still
     gets the client-build reply). */
  const replies = (window.SabdiaForms && window.SabdiaForms.replies) || [];
  const v = (n) => { const e = form.querySelector('[name="' + n + '"]'); return e ? String(e.value || '') : ''; };
  const asked = ((sel ? sel.value : '') + ' ' + v('contact[body]') + ' ' + v('contact[Agency]') + ' ' + (form.getAttribute('data-form-name') || '')).toLowerCase();
  for (let k = 0; k < replies.length; k++) {
    try { if (new RegExp(replies[k].match, 'i').test(asked)) return replies[k].text; } catch (e) { /* bad pattern */ }
  }
  /* Budget and timeline are required whenever they are shown, so the thank-you never has to ask for them (Naomi, 14 Sep 2026). */
  const el = form.querySelector('[data-thanks]');
  return (el && el.textContent.trim()) || form.getAttribute('data-thanks') || null;
}
function thanksHeading(form) {
  const e = form.querySelector('[name="contact[First name]"], [name="contact[first_name]"], [name="first-name"]');
  const first = e ? String(e.value || '').trim().split(/\s+/)[0] : '';
  /* Greeting the way Sabdia's replies open ("Good morning Mark,"); the
     message below carries the thank-you, so the heading never repeats it. */
  const hr = new Date().getHours();
  const greet = hr < 12 ? WORD('morning', 'Good morning') : (hr < 17 ? WORD('afternoon', 'Good afternoon') : WORD('evening', 'Good evening'));
  return first ? greet + ', ' + first.charAt(0).toUpperCase() + first.slice(1) : greet;
}
function mirrorEnquiry(form) {
  const url = window.SabdiaForms && window.SabdiaForms.mirror;
  if (!url) return;
  const out = { 'form-name': form.getAttribute('data-form-name') || 'contact' };
  new FormData(form).forEach((v, k) => {
    const m = /^contact\[(.+)\]$/.exec(k);
    if (!m || k === 'contact[tags]') return;
    let key = m[1].toLowerCase().replace(/\s+/g, '-');
    if (key === 'body') key = 'message';
    if (key === 'interest') key = 'enquiry-type';
    out[key] = String(v);
  });
  try {
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(out), keepalive: true, mode: 'cors' }).catch(() => {});
  } catch (err) { /* mirror is best-effort */ }
}
document.addEventListener('submit', async (e) => {
  const cform = e.target.closest && e.target.closest('form#cform, form.cform');
  if (!cform) return;
  const action = cform.getAttribute('action') || '';
  if (!/\/api\//.test(action)) return; // Shopify-native forms post into a hidden frame (nativeFormInit)
  const native = false;
  e.preventDefault();
  const btn = cform.querySelector('#fsub, .fsub');
  if (!btn || btn.disabled) return;
  const status = cform.querySelector('[data-form-status]');
  const announce = (msg) => { if (status) status.textContent = msg; };
  const label = btn.textContent;
  btn.textContent = WORD('sending', 'Sending…');
  btn.disabled = true;
  btn.style.background = '#6B6860';
  announce('Sending your enquiry…');
  try {
    const body = new URLSearchParams(new FormData(cform)).toString();
    const res = await fetch(action, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'text/html' },
      body,
      credentials: 'same-origin',
      redirect: 'follow'
    });
    if (!res.ok) {
      const detail = await res.json().catch(() => null);
      throw new Error((detail && detail.error) || `Form submit failed: ${res.status}`);
    }
    showThanks(cform, thanksHeading(cform), thanksText(cform));
  } catch (err) {
    const msg = (err && err.message && !/^Form submit failed|Failed to fetch|NetworkError/.test(err.message))
      ? err.message
      : WORD('error', 'Something went wrong — please try again.');
    btn.textContent = label;
    btn.disabled = false;
    btn.style.background = '';
    announce(msg);
    let note = cform.querySelector('.form-error');
    if (!note) { note = document.createElement('p'); note.className = 'form-note form-error'; note.setAttribute('role', 'alert'); btn.insertAdjacentElement('afterend', note); }
    note.textContent = msg;
  }
});

/* Share row: copy-link. */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('.share-copy');
  if (!btn) return;
  /* The button's own word is kept before anything changes it, so a second
     handler (a section's own copy script) can never leave it stuck on Copied. */
  if (!btn.dataset.label) btn.dataset.label = btn.textContent;
  try {
    await navigator.clipboard.writeText(btn.getAttribute('data-share-url') || location.href);
    btn.textContent = btn.getAttribute('data-copied') || WORD('copied', 'Copied');
    clearTimeout(btn._copiedTimer);
    btn._copiedTimer = setTimeout(() => { btn.textContent = btn.dataset.label; }, 1800);
  } catch (err) {
    window.prompt(WORD('copyPrompt', 'Copy this link:'), btn.getAttribute('data-share-url') || location.href);
  }
});

/* Newsletter signup (footer, every page). */
document.addEventListener('submit', async (e) => {
  const form = e.target.closest('#nlForm');
  if (!form) return;
  const nlAction = form.getAttribute('action') || '';
  if (!/\/api\//.test(nlAction)) return; // native newsletter posts into a hidden frame (nativeFormInit)
  const nlNative = false;
  e.preventDefault();
  const btn = form.querySelector('.nl-btn');
  const status = form.querySelector('[data-form-status]');
  const announce = (msg) => { if (status) status.textContent = msg; };
  if (!btn || btn.disabled) return;
  const original = btn.textContent;
  btn.textContent = '…';
  btn.disabled = true;
  try {
    const res = await fetch(nlAction || '/api/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'text/html' },
      body: new URLSearchParams(new FormData(form)).toString(),
      credentials: 'same-origin',
      redirect: 'follow'
    });
    if (nlNative) {
      if (/\/challenge/.test(res.url)) { btn.disabled = false; form.submit(); return; }
      if (!(/customer_posted=true/.test(res.url) || res.redirected)) {
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const err = doc.querySelector('#nlForm [role="status"], #nlForm .errors');
        throw new Error((err && err.textContent.trim()) || 'subscribe failed');
      }
    } else if (!res.ok) {
      const detail = await res.json().catch(() => null);
      throw new Error((detail && detail.error) || 'subscribe failed: ' + res.status);
    }
    const doneEl = form.querySelector('[data-nl-success]');
    const done = (doneEl && doneEl.textContent.trim()) || form.getAttribute('data-success') || WORD('subscribed', 'Thank you — you\'re subscribed.');
    form.innerHTML = '<p class="nl-done">' + done + '</p>';
    announce(done);
  } catch (err) {
    btn.textContent = original;
    btn.disabled = false;
    announce((err && err.message && !/^subscribe failed|Failed to fetch|NetworkError/.test(err.message))
      ? err.message
      : WORD('error', 'Something went wrong — please try again.'));
  }
});

/* ============================================================
   PER PAGE — re-run on every astro:page-load
   ============================================================ */



/* File the enquirer under Customers. Shopify's contact form only emails, so
   the accepted enquiry is copied into the hidden signup form next to it
   (snippet customer-mirror) and that is submitted in the background:
   Customers then holds the person, tagged enquiry · residence · enquiry
   type · newsletter (when "Keep me updated" was ticked). Segments in the
   admin can then be built on those tags. */
/* A residence picked from the Interest list ("AETHER - Hendra") files as
   residence-enquiry: the residence has its own tag already, so the type is
   not a second residence tag (Naomi, 17 Sep 2026: enquiries correctly filtered). */
function enquiryTypeTag(etype) {
  const name = etype.split(' - ')[0].trim().toLowerCase();
  const map = (window.SabdiaForms && window.SabdiaForms.residenceMap) || [];
  if (map.some((r) => r && String(r.title || '').toLowerCase() === name)) return 'residence-enquiry';
  return etype.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
}
function fileCustomer(form, saved) {
  const wrap = form.parentNode && form.parentNode.querySelector('[data-customer-mirror]');
  const mirror = wrap && wrap.querySelector('form');
  if (!mirror) return;
  const submitMirror = () => {
    const em = mirror.querySelector('[name="contact[email]"]');
    em.dispatchEvent(new Event('focusin', { bubbles: true }));
    em.dispatchEvent(new Event('change', { bubbles: true }));
    setTimeout(() => { if (mirror.requestSubmit) mirror.requestSubmit(); else mirror.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true })); }, 1500);
  };
  const setM = (n, v) => { const el = mirror.querySelector('[name="' + n + '"]'); if (el) el.value = v; };
  /* The filing function (supabase/functions/file-enquiry) goes first: it keeps
     every enquiry in the customer's Notes as a dated chain, where Shopify's
     signup form only ever keeps the first message (tested 21 Sep 2026). If it
     is unreachable or refuses, the hidden signup form files the person as
     before, so nobody is lost. */
  const file = (p) => {
    const viaMirror = () => { setM('contact[email]', p.email); setM('contact[first_name]', p.first); setM('contact[last_name]', p.last); setM('contact[tags]', p.tags); setM('contact[note]', p.note || ''); setM('contact[phone]', p.phone || ''); submitMirror(); };
    const url = wrap.getAttribute('data-file-url');
    if (!url) { viaMirror(); return; }
    const tagList = String(p.tags || '').split(',').map((t) => t.trim()).filter(Boolean);
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: p.email, first: p.first, last: p.last, phone: p.phone || '', tags: tagList, optin: tagList.indexOf('newsletter') > -1, note: p.note || '' }) })
      .then((res) => { if (!res.ok) throw new Error('file-enquiry ' + res.status); })
      .catch(viaMirror);
  };
  if (saved) { file(saved); return; }
  const val = (n) => { const el = form.querySelector('[name="' + n + '"]'); return el ? String(el.value || '').trim() : ''; };
  const email = val('contact[email]');
  if (!email) return;
  const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const tags = ['enquiry'];
  const residences = (window.SabdiaForms && window.SabdiaForms.residences) || [];
  const seen = {};
  if (wrap.getAttribute('data-residence')) { tags.push(wrap.getAttribute('data-residence')); seen[wrap.getAttribute('data-residence')] = 1; }
  const blob = Array.from(form.elements).map((el) => (el.type === 'checkbox' && !el.checked) ? '' : String(el.value || '')).join(' ').toLowerCase();
  residences.forEach((h) => { if (!seen[h] && new RegExp('\\b' + h + '\\b').test(blob)) { tags.push(h); seen[h] = 1; } });
  const etype = val('contact[Enquiry type]') || val('contact[Interest]');
  if (etype) tags.push(enquiryTypeTag(etype));
  if (val('contact[Budget range]')) tags.push('budget-' + slug(val('contact[Budget range]')).slice(0, 30));
  if (val('contact[Timeline]')) tags.push('timeline-' + slug(val('contact[Timeline]')).slice(0, 30));
  val('contact[Preferred locations]').split(',').map((x) => x.trim()).filter(Boolean).forEach((x) => tags.push('loc-' + slug(x).slice(0, 30)));
  const optin = form.querySelector('[data-optin]');
  if (optin && optin.checked) tags.push('newsletter');
  const noteOf = () => [
    val('contact[Property]') && 'Residence: ' + val('contact[Property]'),
    (val('contact[Enquiry type]') || val('contact[Interest]')) && 'Interest: ' + (val('contact[Enquiry type]') || val('contact[Interest]')),
    val('contact[Budget range]') && 'Budget: ' + val('contact[Budget range]'),
    val('contact[Timeline]') && 'Timeline: ' + val('contact[Timeline]'),
    val('contact[Preferred locations]') && 'Locations: ' + val('contact[Preferred locations]'),
    val('contact[Flexibility]') && 'Flexibility: ' + val('contact[Flexibility]'),
    val('contact[Agency]') && 'Agency: ' + val('contact[Agency]'),
    val('contact[Phone]') && 'Phone: ' + val('contact[Phone]'),
    val('contact[body]') && 'Message: ' + val('contact[body]'),
    'Via ' + (location.pathname || '/') + ' on ' + new Date().toLocaleDateString('en-AU')
  ].filter(Boolean).join('\n');
  const payload = { email, first: val('contact[First name]') || val('contact[first_name]'), last: val('contact[Last name]') || val('contact[last_name]'), tags: tags.join(', '), note: noteOf(), phone: val('contact[Phone]') };
  try { sessionStorage.removeItem('sabdia-file-customer'); } catch (e) {}
  file(payload);
}
/* Called when an enquiry is about to leave for Shopify's visible challenge
   page: keep what Customers needs, and file it when the visitor returns. */
function stashCustomer(form) {
  try {
    const val = (n) => { const el = form.querySelector('[name="' + n + '"]'); return el ? String(el.value || '').trim() : ''; };
    const wrap = form.parentNode && form.parentNode.querySelector('[data-customer-mirror]');
    if (!wrap || !val('contact[email]')) return;
    const optin = form.querySelector('[data-optin]');
    const etype = val('contact[Enquiry type]') || val('contact[Interest]');
    const tags = ['enquiry'];
    if (wrap.getAttribute('data-residence')) tags.push(wrap.getAttribute('data-residence'));
    const blob = Array.from(form.elements).map((el) => (el.type === 'checkbox' && !el.checked) ? '' : String(el.value || '')).join(' ').toLowerCase();
    ((window.SabdiaForms && window.SabdiaForms.residences) || []).forEach((h) => { if (tags.indexOf(h) === -1 && new RegExp('\\b' + h + '\\b').test(blob)) tags.push(h); });
    if (etype) tags.push(enquiryTypeTag(etype));
    const sl = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    if (val('contact[Budget range]')) tags.push('budget-' + sl(val('contact[Budget range]')).slice(0, 30));
    if (val('contact[Timeline]')) tags.push('timeline-' + sl(val('contact[Timeline]')).slice(0, 30));
    val('contact[Preferred locations]').split(',').map((x) => x.trim()).filter(Boolean).forEach((x) => tags.push('loc-' + sl(x).slice(0, 30)));
    if (optin && optin.checked) tags.push('newsletter');
    const note = ['Residence: ' + val('contact[Property]'), 'Interest: ' + etype, 'Budget: ' + val('contact[Budget range]'), 'Timeline: ' + val('contact[Timeline]'), 'Locations: ' + val('contact[Preferred locations]'), 'Message: ' + val('contact[body]')].filter((l) => !/: $/.test(l)).join('\n');
    sessionStorage.setItem('sabdia-file-customer', JSON.stringify({ email: val('contact[email]'), first: val('contact[First name]'), last: val('contact[Last name]'), tags: tags.join(', '), note, phone: val('contact[Phone]'), t: Date.now() }));
  } catch (e) { /* storage unavailable */ }
}

/* Shopify-native forms ({% form 'contact' %} and {% form 'customer' %}, both
   posting to /contact) without leaving the page. Shopify's spam script
   (hCaptcha) binds to these forms, and on submit it fetches a token and then
   calls form.submit(). That call is intercepted here and sent in the
   background instead; Shopify validates and emails as usual, and the
   visitor sees the thank-you in place. If Shopify still insists on its
   visible challenge page, the real navigation happens and the thank-you
   shows after the return (see the contact_posted handler in initPage). */
function nativeFormInit() {
  document.querySelectorAll('form').forEach((form) => {
    if (form.dataset.nativeWired) return;
    if (!/^\/contact/.test(form.getAttribute('action') || '')) return;
    form.dataset.nativeWired = '1';
    const isNews = form.id === 'nlForm';
    const silent = !!form.closest('[data-customer-mirror]');
    const btn = form.querySelector('#fsub, .fsub, .nl-btn, [type="submit"]');
    const status = form.querySelector('[data-form-status]');
    const announce = (msg) => { if (status) status.textContent = msg; };
    let pending = false, label = btn ? btn.textContent : '';
    const busy = () => {
      pending = true;
      if (silent) return;
      const err = form.querySelector('.form-error'); if (err) err.remove();
      if (btn) { label = btn.textContent; btn.textContent = isNews ? '…' : WORD('sending', 'Sending…'); btn.style.background = '#6B6860'; }
      announce('Sending…');
    };
    const fail = (msg) => {
      pending = false;
      if (silent) return;
      if (form.dataset.quiet) { form.dispatchEvent(new CustomEvent('sabdia:failed', { bubbles: true, detail: msg })); return; }
      if (btn) { btn.textContent = label; btn.style.background = ''; }
      announce(msg);
      let note = form.querySelector('.form-error');
      if (!note) { note = document.createElement('p'); note.className = isNews ? 'sr-status form-error' : 'form-note form-error'; note.setAttribute('role', 'alert'); (btn || form).insertAdjacentElement('afterend', note); }
      note.textContent = msg;
    };
    const done = () => {
      pending = false;
      if (silent) return;
      if (!isNews) fileCustomer(form);
      if (form.dataset.quiet) { form.dispatchEvent(new CustomEvent('sabdia:sent', { bubbles: true })); return; }
      if (isNews) {
        const doneEl = form.querySelector('[data-nl-success]');
        form.innerHTML = '<p class="nl-done">' + ((doneEl && doneEl.textContent.trim()) || WORD('subscribed', 'Thank you — you\'re subscribed.')) + '</p>';
      } else {
        mirrorEnquiry(form);
        let tt = thanksText(form);
        if (form.dataset.cvFailed === '1') tt = (tt || '') + ' ' + WORD('cvUploadFailed', 'Not every document uploaded this time, so please email your CV to [email].');
        showThanks(form, thanksHeading(form), tt);
      }
    };
    /* Careers: every document (one at least; careersFilesInit keeps the
       list) goes to the private careers folder first; the form then carries
       their file names. Only the public key is used, and it can only add
       files to that folder. Returns the names that did not upload. */
    const uploadCv = async () => {
      const cf = form.querySelector('[data-careers-file]'), cp = form.querySelector('[data-careers-path]');
      if (!cf || cf.disabled) return [];
      const files = cf._cvFiles && cf._cvFiles.length ? cf._cvFiles : Array.from(cf.files || []);
      if (!files.length) return [];
      const host = form.closest('[data-careers-upload]') || form;
      const base = host.getAttribute('data-careers-upload'), key = host.getAttribute('data-careers-key');
      if (!base || !key) throw new Error('CV uploads are not set up yet. Please email your CV to ' + (SITE_MAIL() || 'us') + '.');
      const stamp = new Date().toISOString().slice(0, 10) + '-' + Math.random().toString(36).slice(2, 8);
      const results = await Promise.all(files.map(async (f, i) => {
        if (f.size > CV_MAX_BYTES) return { failed: f.name };
        const safe = f.name.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80) || 'cv';
        const name = stamp + '-' + (i + 1) + '-' + safe;
        try {
          const res = await fetch(base + '/' + name, { method: 'POST', headers: { 'apikey': key, 'Authorization': 'Bearer ' + key, 'Content-Type': f.type || 'application/octet-stream', 'x-upsert': 'false' }, body: f });
          return res.ok ? { name } : { failed: f.name };
        } catch (e) { return { failed: f.name }; }
      }));
      const names = results.filter((r) => r.name).map((r) => r.name);
      const failed = results.filter((r) => r.failed).map((r) => r.failed);
      /* The bucket is private, so a name alone leaves the document to be
         hunted down in Supabase. careers-cv signs each file it has just
         received and the enquiry email carries the link instead. If it is
         unreachable the names go through as before - the files are safely
         uploaded either way. */
      let carried = names.join(', ');
      const link = host.getAttribute('data-careers-link');
      if (link && names.length) {
        try {
          const res = await fetch(link, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ files: names }) });
          if (res.ok) {
            const out = await res.json();
            const got = (out && out.links) || [];
            if (got.length) carried = got.map((l) => l.name + ': ' + l.url).join(' | ');
          }
        } catch (e) { /* names it is */ }
      }
      if (cp) cp.value = carried + (failed.length ? (names.length ? ' | ' : '') + 'UPLOAD FAILED: ' + failed.join(', ') + ' - please ask the applicant to email these' : '');
      return failed;
    };
    const send = async () => {
      try {
        /* If an upload fails the enquiry still goes, flagged, and the
           thank-you asks for the CV by email - a blocked form loses the
           applicant altogether. */
        try { form.dataset.cvFailed = (await uploadCv()).length ? '1' : ''; }
        catch (err) { const cp = form.querySelector('[data-careers-path]'); if (cp) cp.value = 'UPLOAD FAILED - please ask the applicant to email their CV'; form.dataset.cvFailed = '1'; }
        const res = await fetch(form.getAttribute('action').split('#')[0], {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'text/html' },
          body: new URLSearchParams(new FormData(form)).toString(),
          credentials: 'same-origin',
          redirect: 'follow'
        });
        if (/\/challenge/.test(res.url)) {
          /* Shopify's visible check; it holds the enquiry and returns here.
             The hidden customer mirror must never take the page there: the
             visitor is sitting on their thank-you and the enquiry itself has
             already been emailed. Verified 18 Sep 2026: Shopify answers every
             mirror POST with /challenge, so this branch was firing on every
             enquiry and bouncing the visitor to a captcha page (and then to
             the home page), with no customer filed either way. Give up
             quietly instead; filing is moving to the Admin API. */
          if (silent) { pending = false; return; }
          if (!isNews) stashCustomer(form);
          if (form.dataset.quiet) { try { sessionStorage.setItem('cc-pending', '1'); } catch (e) {} }
          location.assign(res.url);
          return;
        }
        const ok = isNews ? /customer_posted=true/.test(res.url) : /contact_posted=true/.test(res.url);
        if (ok) { done(); return; }
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const alert = doc.querySelector((isNews ? '#nlForm' : '#cform') + ' [role="alert"], .errors');
        if (alert && alert.textContent.trim()) throw new Error(alert.textContent.trim());
        if (/CAPTCHA/i.test(doc.title || '')) throw new Error(WORD('spam', 'The spam check did not pass — please press the button again.'));
        if (res.ok && !alert && !/\/contact/.test(res.url)) { done(); return; }
        throw new Error(WORD('error', 'Something went wrong — please try again.'));
      } catch (err) {
        fail((err && err.message && !/Failed to fetch|NetworkError/.test(err.message)) ? err.message : WORD('error', 'Something went wrong — please try again.'));
      }
    };
    /* Shopify's spam script ends with form.submit(); take it from here. */
    form.submit = function () { if (!pending) busy(); send(); };
    form.addEventListener('submit', (e) => {
      if (pending) { e.preventDefault(); return; }
      if (!form.checkValidity()) return;
      busy();
      /* Not under Shopify's spam script (protection off, or its script did
         not load): post directly. Otherwise let it run; it calls submit(). */
      if (!form.dataset.cptcha && !form.dataset.hcaptchaBound && !form.dataset.recaptchaBound) { e.preventDefault(); send(); }
    });
  });
}


/* Careers documents (Naomi, 15 Sep 2026: more than one PDF, at least one
   required). Each pick adds to the list instead of replacing it, every
   document shows with a Remove button, and the file field stays required,
   so the form cannot send without one. Browsers that cannot hold a list
   keep the native field (several files chosen at once still work). */
const CV_MAX_BYTES = 10 * 1024 * 1024, CV_MAX_FILES = 10;
/* Email addresses printed by snippets/cloaked-email and cloak-email-text:
   the page code holds the two halves and a stylesheet-drawn "@", so bots
   reading the source find no address. Here, in a real browser, the link
   becomes a mailto and the words become the true address (so it copies and
   pastes whole). Guarded per element: the theme editor re-runs this. */
function cloakedMailInit(root) {
  (root || document).querySelectorAll('.js-mail, .js-mail-text').forEach((el) => {
    if (el.dataset.mailReady) return;
    const u = el.dataset.mailU, d = el.dataset.mailD;
    if (!u || !d) return;
    el.dataset.mailReady = '1';
    el.textContent = u + '@' + d;
    if (el.tagName === 'A') el.setAttribute('href', 'mailto:' + u + '@' + d);
  });
}

function careersFilesInit() {
  document.querySelectorAll('[data-careers-file]').forEach((input) => {
    if (input.dataset.cvInit) return;
    input.dataset.cvInit = '1';
    const list = input.parentNode.querySelector('[data-careers-list]');
    const kept = [];
    input._cvFiles = kept;
    let canKeep = true;
    try { new DataTransfer().items.add(new File([''], 'x.pdf')); } catch (e) { canKeep = false; }
    const sizeOf = (b) => b >= 1048576 ? (b / 1048576).toFixed(1) + ' MB' : Math.max(1, Math.round(b / 1024)) + ' KB';
    const render = (note) => {
      if (canKeep) { const dt = new DataTransfer(); kept.forEach((f) => dt.items.add(f)); input.files = dt.files; }
      if (!list) return;
      list.textContent = '';
      kept.forEach((f, i) => {
        const li = document.createElement('li');
        const name = document.createElement('span'); name.textContent = f.name;
        const size = document.createElement('small'); size.textContent = sizeOf(f.size);
        li.append(name, size);
        if (canKeep) {
          const rm = document.createElement('button');
          rm.type = 'button'; rm.className = 'cv-remove'; rm.textContent = WORD('cvRemove', 'Remove');
          rm.setAttribute('aria-label', WORD('cvRemove', 'Remove') + ' ' + f.name);
          rm.addEventListener('click', () => { kept.splice(i, 1); render(); input.focus(); });
          li.appendChild(rm);
        }
        list.appendChild(li);
      });
      if (note) { const li = document.createElement('li'); li.className = 'cv-skip'; li.setAttribute('role', 'alert'); li.textContent = note; list.appendChild(li); }
      list.hidden = !list.children.length;
    };
    input.addEventListener('change', () => {
      const picked = Array.from(input.files || []);
      if (!canKeep) { kept.splice(0, kept.length, ...picked); render(); return; }
      const skipped = [];
      picked.forEach((f) => {
        if (kept.some((k) => k.name === f.name && k.size === f.size)) return;
        if (!/\.(pdf|docx?)$/i.test(f.name)) skipped.push(f.name + ' ' + WORD('cvNotDoc', 'is not a PDF or Word file'));
        else if (f.size > CV_MAX_BYTES) skipped.push(f.name + ' ' + WORD('cvTooBig', 'is over 10 MB'));
        else if (kept.length >= CV_MAX_FILES) skipped.push(f.name + ' (' + CV_MAX_FILES + ' ' + WORD('cvTooMany', 'documents at most') + ')');
        else kept.push(f);
      });
      render(skipped.length ? WORD('cvLeftOut', 'Left out:') + ' ' + skipped.join('; ') + '.' : '');
    });
  });
}

/* Pre-qualification. The Interest choice decides what else is asked:
   a residence (QASR - Coorparoo) or For Sale opens budget, timeline and
   locations; anything else (custom build, agent, media, general) hides
   them. Budget and timeline become required when shown; locations are
   required for a general For Sale enquiry and optional for a residence,
   whose suburb is pre-selected. Residence pages always show the block. */
function prequalInit() {
  const map = (window.SabdiaForms && window.SabdiaForms.residenceMap) || [];
  document.querySelectorAll('[data-prequal]').forEach((wrap) => {
    if (wrap.dataset.pqInit) return;
    wrap.dataset.pqInit = '1';
    const form = wrap.closest('form');
    if (!form) return;
    const sel = form.querySelector('select[name="contact[Interest]"], select[name="contact[Enquiry type]"], select[name="enquiry-type"]');
    const budget = wrap.querySelector('[data-prequal-budget]'), time = wrap.querySelector('[data-prequal-timeline]');
    const chips = Array.from(wrap.querySelectorAll('[data-prequal-loc]')), hiddenLoc = wrap.querySelector('[data-prequal-locations]');
    const locLabel = wrap.querySelector('[data-prequal-loclabel]'), locReq = locLabel && locLabel.querySelector('[data-prequal-req]');
    const pageSuburb = wrap.getAttribute('data-residence-suburb') || '';
    const norm = (t) => String(t || '').toLowerCase().replace(/\s+/g, ' ').trim();
    const residenceFor = (value) => {
      const v = norm(value);
      if (!v) return null;
      return map.find((r) => v.indexOf(norm(r.title)) !== -1 || (r.handle && new RegExp('\\b' + r.handle + '\\b').test(v))) || null;
    };
    const syncLoc = () => {
      if (hiddenLoc) hiddenLoc.value = chips.filter((c) => c.checked).map((c) => c.value).join(', ');
      if (chips.length) chips[0].setCustomValidity(wrap.dataset.locRequired === '1' && !chips.some((c) => c.checked) ? WORD('pickLocation', 'Please choose at least one location') : '');
    };
    chips.forEach((c) => c.addEventListener('change', syncLoc));
    const apply = () => {
      let mode = 'none', suburb = '';
      if (pageSuburb) {
        /* Residence page: a general question needs none of this; an inspection
           or a registration of interest brings the buyer questions up. */
        const t = sel ? sel.value : '';
        if (!sel || /inspect|interest|buy|purchase|offer/i.test(t)) { mode = 'residence'; suburb = pageSuburb; }
      }
      else if (sel) {
        /* Contact page (Naomi, 14 Sep 2026): the buyer questions only for a
           specific residence, a viewing, or For Sale - Current Residences. */
        const r = residenceFor(sel.value);
        if (r) { mode = 'residence'; suburb = r.suburb || ''; }
        else if (/viewing|inspect|for sale|current residence/i.test(sel.value)) mode = 'forsale';
      } else { mode = 'forsale'; }
      /* Careers: a CV instead of the buyer questions. */
      const cw = form.querySelector('[data-careers-wrap]'), cf = form.querySelector('[data-careers-file]');
      const isCareers = !!sel && /career|job|employ|work with|position|vacanc/i.test(sel.value);
      if (cw) cw.hidden = !isCareers;
      if (cf) { cf.required = isCareers; cf.disabled = !isCareers; }
      if (isCareers) mode = 'none';
      const show = mode !== 'none';
      wrap.hidden = !show;
      if (budget) budget.required = show;
      if (time) time.required = show;
      chips.forEach((c) => { if (c.dataset.locked === '1') { c.checked = false; c.disabled = false; c.dataset.locked = ''; c.parentNode.classList.remove('chip-home'); } });
      if (mode === 'residence' && suburb) {
        const home = chips.find((c) => norm(c.value) === norm(suburb));
        if (home) { home.checked = true; home.dataset.locked = '1'; home.parentNode.classList.add('chip-home'); }
      }
      wrap.dataset.locRequired = mode === 'forsale' ? '1' : '0';
      if (locLabel) {
        locLabel.firstChild.textContent = mode === 'residence' ? locLabel.getAttribute('data-label-residence') : locLabel.getAttribute('data-label-forsale');
        if (locReq) locReq.hidden = mode !== 'forsale';
      }
      syncLoc();
    };
    if (sel) sel.addEventListener('change', apply);
    apply();
  });
}


/* Sabdia Concierge (section concierge.liquid). Scripted only: every reply is
   a Chat answer block, a fact from Products or a step of the enquiry, never
   an AI. No price is passed in, and scrubMoney strips any currency amount
   from a bot message regardless; the visitor's own budget chip is the only
   money that ever appears, and only as their message. The enquiry runs step
   by step and posts through the hidden Shopify contact form #ccContact. */
function conciergeInit() {
  const cc = document.querySelector('[data-concierge]');
  if (!cc || cc.dataset.init) return;
  cc.dataset.init = '1';
  const parseJSON = (t, d) => { try { return JSON.parse(t); } catch (e) { return d; } };
  const residences = parseJSON(cc.getAttribute('data-residences') || '[]', []);
  const forSale = residences.filter((r) => !/sold/i.test(r.status || ''));
  const raw = parseJSON((cc.querySelector('[data-concierge-qa]') || {}).textContent || '[]', []);
  const qa = [];
  for (let i = 0; i < raw.length; i += 6) qa.push({ q: raw[i] || '', k: raw[i + 1] || '', a: raw[i + 2] || '', link: raw[i + 3] || '', cat: raw[i + 4] || 'other' });
  const locations = (cc.getAttribute('data-locations') || '').split(',').map((x) => x.trim()).filter(Boolean);
  const budgets = (cc.getAttribute('data-budgets') || '').split('|').map((x) => x.trim()).filter(Boolean);
  const timelines = (cc.getAttribute('data-timelines') || '').split('|').map((x) => x.trim()).filter(Boolean);
  const log = cc.querySelector('#ccLog'), panel = cc.querySelector('#ccPanel'), launch = cc.querySelector('#ccLaunch');
  const input = cc.querySelector('#ccText'), chatForm = cc.querySelector('#ccForm');
  const contact = cc.querySelector('#ccContact');
  const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const linkify = (t) => esc(t).replace(/(https?:\/\/[^\s]+|\/(?:pages|products|collections)\/[a-z0-9\-\/]+)/g, (m) => '<a href="' + m + '">' + m.replace(/^https?:\/\/[^/]+/, '') + '</a>');
  /* Hard guard: no bot message may carry a currency amount. "$0", "$0.00",
     "AUD 0", "$4,500,000", "$5m" - anything with a currency mark and a
     number is removed before it reaches the log. The visitor's own messages
     (who === 'me', which is how a chosen budget chip is echoed) are left
     as typed. */
  const scrubMoney = (html) => String(html)
    .replace(/\b(?:AUD?|AU\$|A\$)\s?\$?\s?\d[\d,]*(?:\.\d+)?\s?(?:k|m|million|thousand)?\b/gi, '')
    .replace(/\$\s?\d[\d,]*(?:\.\d+)?\s?(?:k|m|million|thousand)?\b/gi, '')
    .replace(/[ \t]{2,}/g, ' ').replace(/\s+([.,;:!?)])/g, '$1').trim();
  const scroll = () => { log.scrollTop = log.scrollHeight; };
  const say = (html, who) => { const d = document.createElement('div'); d.className = 'cc-msg ' + (who || 'bot'); d.innerHTML = who === 'me' ? html : scrubMoney(html); log.appendChild(d); scroll(); return d; };
  const options = (items, onPick, ghostLast) => {
    const wrap = document.createElement('div'); wrap.className = 'cc-opts';
    items.forEach((label, n) => {
      const b = document.createElement('button'); b.type = 'button'; b.className = 'cc-opt' + (ghostLast && n === items.length - 1 ? ' ghost' : ''); b.textContent = label;
      b.addEventListener('click', () => { wrap.remove(); onPick(label, n); });
      wrap.appendChild(b);
    });
    log.appendChild(wrap); scroll(); return wrap;
  };
  const multi = (items, doneLabel, onDone) => {
    const chosen = new Set();
    const wrap = document.createElement('div'); wrap.className = 'cc-opts';
    items.forEach((label) => { const b = document.createElement('button'); b.type = 'button'; b.className = 'cc-opt'; b.textContent = label; b.addEventListener('click', () => { b.classList.toggle('on'); if (chosen.has(label)) chosen.delete(label); else chosen.add(label); }); wrap.appendChild(b); });
    const d = document.createElement('button'); d.type = 'button'; d.className = 'cc-opt ghost'; d.textContent = doneLabel; d.addEventListener('click', () => { wrap.remove(); onDone(Array.from(chosen)); }); wrap.appendChild(d);
    log.appendChild(wrap); scroll();
  };
  const typing = async (ms) => { const d = document.createElement('div'); d.className = 'cc-typing'; d.textContent = '…'; log.appendChild(d); scroll(); await new Promise((r) => setTimeout(r, ms || 450)); d.remove(); };
  const norm = (t) => String(t || '').toLowerCase().replace(/[^a-z0-9$\s]/g, ' ').replace(/\s+/g, ' ').trim();
  const STOP = new Set('you your can could the and for are with this that what how does have has any our its get from into will would like want need please hello hey thanks tell more about there here they them then than some just also very really when where which who why yes not but all one two new house home homes property properties sabdia'.split(' '));
  const words = (t) => norm(t).split(' ').filter((w) => w.length > 2 && !STOP.has(w));
  /* Residence answer, built live from the product data (no price field is passed). */
  const residenceText = (r) => {
    const facts = [r.beds && r.beds + ' bedrooms', r.baths && r.baths + ' bathrooms', r.cars && r.cars + '-car garage', r.build && r.build + ' sqm build', r.land && r.land + ' sqm land'].filter(Boolean).join(', ');
    const feats = (r.features || '').split(',').map((x) => x.trim()).filter(Boolean).slice(0, 6).join(', ');
    const status = /sold/i.test(r.status || '') ? ' (' + r.status + ')' : '';
    return r.title + ' in ' + r.suburb + status + '. ' + (r.headline ? r.headline.replace(/\s+/g, ' ').replace(/\s+([.,;:!?])/g, '$1').trim() + ' ' : '') + (facts ? facts + '. ' : '') + (feats ? 'Highlights: ' + feats + '. ' : '') + 'See ' + r.url + ' or tell me if you would like to enquire.';
  };
  const findResidence = (text) => { const n = norm(text); return residences.find((r) => n.indexOf(norm(r.title)) !== -1 || n.indexOf(r.handle) !== -1) || null; };
  /* Intents, read from typed text or from a Chat answer's question and
     keywords. The enquiry type is written to contact[Enquiry type], so a
     viewing is "Request a Viewing" exactly as the contact form's interest
     list has it. */
  const viewingIntent = /\b(inspect\w*|view|viewing|book\w*|appointment|open home|visit\w*|tour|look (?:through|at|inside|around))\b/i;
  const priceIntent = /\b(price\w*|cost\w*|how much|worth|asking|expensive|afford\w*|valuation)\b/i;
  const enquiryIntent = /\b(enquir|inquir|price|pricing|cost|how much|inspect|view\b|viewing|book|tour|visit|appointment|interested|register|buy|purchase|contact|call me|floor ?plan|brochure)/i;
  const personIntent = /\b(person|human|someone|staff|talk to|speak to|call)\b/i;
  const intentFor = (m) => {
    const t = m.q + ' ' + m.k;
    if (viewingIntent.test(t)) return 'Request a Viewing';
    if (priceIntent.test(t)) return 'Request Price Guide';
    if (/\b(release|upcoming)\b/i.test(t)) return 'Upcoming Releases';
    if (m.cat === 'agents') return 'Agent Access';
    if (m.cat === 'builds' || m.cat === 'other') return 'General Enquiry';
    return 'Register Interest';
  };
  const bestMatch = (text) => {
    const ws = words(text); if (!ws.length) return null;
    let best = null, bestScore = 0;
    const hit = (bag, w) => bag.some((b) => b === w || (w.length > 4 && b.length > 4 && (b.indexOf(w.slice(0, 5)) === 0 || w.indexOf(b.slice(0, 5)) === 0)));
    qa.forEach((item) => {
      const kw = words(item.k), qw = words(item.q);
      let score = 0; ws.forEach((w) => { if (hit(kw, w)) score += 2; else if (hit(qw, w)) score += 1; });
      if (score > bestScore) { bestScore = score; best = item; }
    });
    return bestScore >= 2 ? best : null;
  };
  /* Enquiry, step by step. */
  const state = {};
  const setC = (n, v) => { const el = contact && contact.querySelector('[name="' + n + '"]'); if (el) el.value = v || ''; };
  let step = null;
  const ask = (q, key, opts) => { say(esc(q)); step = { key, opts }; if (opts) options(opts.items, (label) => answerStep(label), opts.ghostLast); input.focus(); };
  const lastQuestion = () => state.intent === 'Request a Viewing' ? 'Which days or times suit you for a viewing? (type "no" if you are not sure yet)' : 'Anything else we should know? (type "no" if not)';
  const afterPhone = () => {
    if (state.kind === 'general') ask('How can we help? Tell us a little about your enquiry.', 'message');
    else if (state.residence) askBudget();
    else ask('Which residence are you interested in?', 'residence', { items: forSale.map((r) => r.title + ' - ' + r.suburb).concat(['Not sure yet']), ghostLast: true });
  };
  const answerStep = async (value) => {
    if (!step) return;
    const { key } = step; state[key] = value; say(esc(value), 'me'); step = null;
    await typing(350);
    if (key === 'name') ask('And your email address?', 'email');
    else if (key === 'email') { if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) { ask('That email does not look right. Could you check it?', 'email'); return; } ask('And the best phone number to reach you on?', 'phone'); }
    else if (key === 'phone') afterPhone();
    else if (key === 'residence') askBudget();
    else if (key === 'budget') ask('And your timeline to purchase?', 'timeline', { items: timelines });
    else if (key === 'timeline') { say(esc('Which locations would you consider? Tap all that apply.')); multi(locations, 'Done', async (picked) => { state.locations = picked.join(', '); say(esc(picked.length ? picked.join(', ') : 'No preference'), 'me'); await typing(300); ask(lastQuestion(), 'message'); }); }
    else if (key === 'message') submitEnquiry();
  };
  const askBudget = () => ask('To help us give you the most relevant information, what is your approximate budget range?', 'budget', { items: budgets });
  const startEnquiry = (residence) => {
    if (residence) state.residence = residence.title + ' - ' + residence.suburb;
    /* A general matter (a trade, a job, the press, a build request) needs no residence or budget; a release enquiry needs no residence. */
    if (state.intent === 'General Enquiry' && !state.kind) state.kind = 'general';
    if (state.intent === 'Upcoming Releases' && !state.kind && !state.residence) { state.kind = 'sales'; state.residence = 'Not sure yet'; }
    if (state.residence || state.kind) { if (state.name && state.email && state.phone) { afterPhone(); return; } ask('May I take your name?', 'name'); return; }
    /* First: what is it about? A residence, a home sales enquiry in general, or something else. */
    say(esc('What is your enquiry about?'));
    options(forSale.map((r) => r.title + ' - ' + r.suburb).concat(['A home for sale, not sure which yet', 'Something else']), (label, n) => {
      say(esc(label), 'me');
      if (n < forSale.length) { state.residence = label; state.kind = 'residence'; }
      else if (n === forSale.length) { state.kind = 'sales'; state.residence = 'Not sure yet'; }
      else { state.kind = 'general'; state.intent = 'General Enquiry'; }
      typing(300).then(() => ask('May I take your name?', 'name'));
    });
  };
  const submitEnquiry = async () => {
    if (!contact) { say(esc('Please use our contact page: /pages/contact')); return; }
    const parts = String(state.name || '').trim().split(/\s+/);
    setC('contact[First name]', parts[0] || ''); setC('contact[Last name]', parts.slice(1).join(' '));
    setC('contact[email]', state.email); setC('contact[Phone]', state.phone);
    setC('contact[Property]', state.residence && state.residence !== 'Not sure yet' ? state.residence : '');
    setC('contact[Enquiry type]', state.intent || 'Register Interest');
    setC('contact[Budget range]', state.budget); setC('contact[Timeline]', state.timeline); setC('contact[Preferred locations]', state.locations || '');
    setC('contact[body]', (state.message && !/^no$/i.test(state.message)) ? state.message : 'Enquiry via concierge chat');
    say(esc('Sending…'));
    const em = contact.querySelector('[name="contact[email]"]');
    em.dispatchEvent(new Event('focusin', { bubbles: true })); em.dispatchEvent(new Event('change', { bubbles: true }));
    setTimeout(() => { if (contact.requestSubmit) contact.requestSubmit(); else contact.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true })); }, 1200);
  };
  const reset = () => { step = null; Object.keys(state).forEach((k) => delete state[k]); };
  if (contact) {
    contact.addEventListener('sabdia:sent', async () => { await typing(300); const first = (state.name || '').split(/\s+/)[0]; const thanks = cc.getAttribute('data-thanks') || 'Thank you. We will be in touch.'; say(esc(first ? 'Thank you, ' + first + '. ' + thanks.replace(/^\s*thank you\.\s*/i, '') : thanks)); options(['Ask another question'], () => showOpening()); reset(); });
    contact.addEventListener('sabdia:failed', (e) => { say(esc(e.detail || 'Something went wrong. Please try again or use /pages/contact.')); options(['Try again'], () => submitEnquiry()); });
  }
  const handOver = () => {
    cc.classList.add('handoff');
    const btn = document.querySelector('#ShopifyChat button, shopify-chat button, [id^="shopify-chat"] button, .shopify-chat-launcher, #ShopifyChat');
    if (btn) { closePanel(); setTimeout(() => btn.click(), 150); }
    else say('You can reach the team at <a href="/pages/contact">/pages/contact</a>.');
  };
  const priceQA = qa.find((x) => /\bprice/i.test(x.k)) || qa.find((x) => /\bprice/i.test(x.q)) || null;
  const viewingQA = qa.find((x) => /\bviewing\b/i.test(x.q)) || qa.find((x) => /\b(viewing|inspect)/i.test(x.k)) || null;
  const PRICE_TEXT = 'We talk through pricing directly rather than publishing a guide. Leave your details and a member of our team will be in touch.';
  const VIEW_TEXT = 'Every residence is shown by private appointment. Leave your details and a member of our team will arrange a time.';
  /* The answer first, then the offer to leave details: a typed question
     about the price or a viewing of a named residence reads why pricing is
     discussed directly (never a figure) before any step begins. */
  const answerThen = (item, r, intent, fallbackText) => {
    say(item ? linkify(item.a) : esc(fallbackText));
    const items = ['Leave my details'].concat(r ? ['Tell me about ' + r.title] : []).concat(['Back to questions']);
    options(items, (label, n) => { if (n === 0) { state.intent = intent; startEnquiry(r); } else if (r && n === 1) residenceCard(r); else showOpening(); });
  };
  const residenceCard = (r, backLabel) => {
    say(linkify(residenceText(r)));
    options(['Enquire about ' + r.title, 'Request a viewing', backLabel || 'Ask something else'], (label, n) => { if (n === 2) { showOpening(); return; } state.intent = n === 1 ? 'Request a Viewing' : 'Register Interest'; startEnquiry(r); });
  };
  const handle = async (text) => {
    /* Typing instead of tapping: the button groups already offered are stale now, so clear them from the transcript. */
    log.querySelectorAll('.cc-opts').forEach((w) => w.remove());
    if (step && /^(cancel|stop|back|start over|never ?mind)\.?$/i.test(text.trim())) { reset(); say(esc('Of course. Choose a topic below or ask another question.')); showOpening(); return; }
    if (step) { await answerStep(text); return; }
    say(esc(text), 'me');
    await typing(500);
    const r = findResidence(text);
    if (personIntent.test(text) && !enquiryIntent.test(text)) { handOver(); return; }
    if (r && priceIntent.test(text)) { answerThen(priceQA, r, 'Request Price Guide', PRICE_TEXT); return; }
    if (r && viewingIntent.test(text)) { answerThen(viewingQA, r, 'Request a Viewing', VIEW_TEXT); return; }
    if (r && !enquiryIntent.test(text)) { residenceCard(r); return; }
    let m = bestMatch(text);
    if (!m && /\bsabdia\b/i.test(text) && !enquiryIntent.test(text)) m = qa.find((x) => x.cat === 'about') || null; // "tell me about Sabdia" is all stop words
    if (m) { say(linkify(m.a)); afterAnswer(m, r); return; }
    if (enquiryIntent.test(text)) {
      if (priceIntent.test(text)) { answerThen(priceQA, null, 'Request Price Guide', PRICE_TEXT); return; }
      state.intent = viewingIntent.test(text) ? 'Request a Viewing' : 'Register Interest';
      startEnquiry(r); return;
    }
    say(esc(cc.getAttribute('data-fallback') || 'Our team can help with that.'));
    options(['Leave an enquiry', cc.getAttribute('data-person') || 'Chat with a person', 'Back to questions'], (label, n) => { if (n === 0) startEnquiry(null); else if (n === 1) handOver(); else showOpening(); });
  };
  const afterAnswer = (m, r) => {
    const go = () => { state.intent = intentFor(m); startEnquiry(r); };
    if (m.link === 'enquire') options(['Leave my details', 'Back to questions'], (label, n) => { if (n === 0) go(); else showOpening(); });
    else if (m.link) { say('<a href="' + esc(m.link) + '">Open ' + esc(m.link) + '</a>'); options(['Leave an enquiry', 'Back to questions'], (label, n) => { if (n === 0) go(); else showOpening(); }); }
    else options(['Leave an enquiry', 'Back to questions'], (label, n) => { if (n === 0) go(); else showOpening(); });
  };
  const CATS = ['about', 'residences', 'buying', 'builds', 'agents', 'other'];
  const catNames = (cc.getAttribute('data-cats') || '').split('|');
  const person = cc.getAttribute('data-person') || 'Chat with a person';
  const askItem = (item) => { say(esc(item.q), 'me'); typing(400).then(() => { say(linkify(item.a)); afterAnswer(item, null); }); };
  const showQuestions = (list, back) => {
    const items = list.map((x) => x.q).concat([back ? 'Back' : person]);
    options(items, (label, n) => { if (n === items.length - 1) { if (back) { showOpening(); } else { say(esc(label), 'me'); handOver(); } return; } askItem(list[n]); }, true);
  };
  const showOpening = () => {
    if (!step) reset(); // back at the menu: nothing from an earlier path carries into the next enquiry
    const count = parseInt(cc.getAttribute('data-buttons') || '5', 10);
    if ((cc.getAttribute('data-menu') || 'categories') !== 'categories') { showQuestions(qa.slice(0, count), false); return; }
    const present = CATS.filter((c) => qa.some((x) => x.cat === c));
    const items = present.map((c) => catNames[CATS.indexOf(c)] || c).concat([person]);
    options(items, (label, n) => {
      if (n === items.length - 1) { say(esc(label), 'me'); handOver(); return; }
      say(esc(label), 'me');
      const c = present[n];
      if (c === 'residences') {
        /* Residences: one button per home for sale, plus the residence questions. */
        const homes = forSale.map((r) => r.title + ' - ' + r.suburb);
        const qs = qa.filter((x) => x.cat === c);
        const list = homes.concat(qs.map((x) => x.q)).concat(['Back']);
        options(list, (lab, i) => { if (i === list.length - 1) { showOpening(); return; } if (i < homes.length) { const r = forSale[i]; say(esc(lab), 'me'); typing(400).then(() => residenceCard(r, 'Back')); return; } askItem(qs[i - homes.length]); }, true);
        return;
      }
      showQuestions(qa.filter((x) => x.cat === c), true);
    }, true);
  };
  let opened = false;
  const openPanel = () => { panel.hidden = false; cc.classList.add('open'); launch.setAttribute('aria-expanded', 'true'); if (!opened) { opened = true; say(esc(cc.getAttribute('data-greeting') || 'Hello.')); showOpening(); } setTimeout(() => input.focus(), 100); };
  const closePanel = () => { panel.hidden = true; cc.classList.remove('open'); launch.setAttribute('aria-expanded', 'false'); };
  launch.addEventListener('click', openPanel);
  cc.querySelector('#ccClose').addEventListener('click', closePanel);
  const dismiss = cc.querySelector('#ccDismiss');
  if (dismiss) dismiss.addEventListener('click', () => { closePanel(); cc.classList.add('hidden-by-user'); try { sessionStorage.setItem('cc-hidden', '1'); } catch (e) {} });
  try { if (sessionStorage.getItem('cc-hidden')) cc.classList.add('hidden-by-user'); } catch (e) {}
  document.addEventListener('click', (e) => { if (!panel.hidden && !cc.contains(e.target)) closePanel(); });
  chatForm.addEventListener('submit', (e) => { e.preventDefault(); const t = input.value.trim(); if (!t) return; input.value = ''; handle(t); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !panel.hidden) closePanel(); });
  const delay = parseInt(cc.getAttribute('data-open-delay') || '0', 10);
  if (delay > 0) setTimeout(() => { if (panel.hidden && !sessionStorage.getItem('cc-auto')) { try { sessionStorage.setItem('cc-auto', '1'); } catch (e) {} openPanel(); } }, delay * 1000);
  if (/[?&]contact_posted=true/.test(location.search) && contact && sessionStorage.getItem('cc-pending')) { try { sessionStorage.removeItem('cc-pending'); } catch (e) {} openPanel(); say(esc(cc.getAttribute('data-thanks') || 'Thank you. We will be in touch.')); }
}

/* The reload after Shopify's spam check (…?contact_posted=true) lands on the
   thank-you in one go; the browser's own scroll restore would pull it away. */
if (/[?&]contact_posted=true/.test(location.search)) { try { history.scrollRestoration = 'manual'; } catch (e) { /* not supported */ } }

function initPage() {
  nativeFormInit();
  prequalInit();
  careersFilesInit();
  cloakedMailInit();
  conciergeInit();
  if (window.SabdiaForms) window.SabdiaForms.preview = { thanksText, thanksHeading }; // lets staff test replies from the console
  /* After Shopify accepts an enquiry it reloads the page with
     ?contact_posted=true. Show the thank-you where the form was and bring
     it into view (the form sits below the fold on most pages). */
  if (/[?&]contact_posted=true/.test(location.search)) {
    var thanks = document.getElementById('formThanks');
    var fyh = document.querySelector('form[data-fyh]');
    if (!thanks && fyh) {
      thanks = document.createElement('div');
      thanks.className = 'form-thanks'; thanks.id = 'formThanks'; thanks.setAttribute('role', 'status'); thanks.tabIndex = -1;
      thanks.innerHTML = '<div class="form-thanks-k" aria-hidden="true">&#10003;</div><h3 class="form-thanks-h">' + WORD('thanksHeading', 'Thank you') + '</h3><p class="form-thanks-p">' + WORD('thanksFyh', 'We have your answers and will be in touch within a business day.') + '</p>';
      fyh.parentNode.insertBefore(thanks, fyh); fyh.hidden = true;
    }
    try {
      const saved = JSON.parse(sessionStorage.getItem('sabdia-file-customer') || 'null');
      const cform = document.getElementById('cform') || document.querySelector('form.cform');
      if (saved && cform && Date.now() - saved.t < 30 * 60 * 1000) { sessionStorage.removeItem('sabdia-file-customer'); setTimeout(() => fileCustomer(cform, saved), 800); }
    } catch (e) { /* no storage */ }
    if (thanks) {
      thanks.classList.add('vis');
      /* Land on it at once - the page must not open at the top and then
         glide down (Naomi, 14 Sep 2026). scrollRestoration is set to manual
         at the top of this file for this reload, so the browser does not
         fight it either. */
      thanks.scrollIntoView({ behavior: 'instant', block: 'center' });
      thanks.focus({ preventScroll: true });
      try { history.replaceState(null, '', location.pathname + location.hash); } catch (e) {}
    }
  }

  // Refresh the cursor element refs for this page's Nav.
  curDot = document.getElementById('cur');
  curRing = document.getElementById('cur-r');

  // ── BRISBANE CLOCK (UTC+10, no DST) ───────────────────────
  const fmtBris = () => {
    try {
      return new Intl.DateTimeFormat('en-AU', {
        timeZone: 'Australia/Brisbane',
        hour: '2-digit', minute: '2-digit', hour12: false
      }).format(new Date()) + ' BNE';
    } catch (e) { return 'BNE'; }
  };
  const navTime = document.getElementById('navTime');
  if (navTime) {
    navTime.textContent = fmtBris();
    onInterval(() => { navTime.textContent = fmtBris(); }, 30000);
  }

  // ── LOADER ────────────────────────────────────────────────
  // Homepage-only, first visit per session (see Nav.astro + Base.astro).
  //
  // This used to hang off `window.load`, which waits for every image on
  // the page — on a slideshow hero that is seconds of cream, and the
  // visitor is held behind a wash long after the first slide is actually
  // paintable. Now whichever comes first wins: the load event, or a hard
  // cap. The cap means a slow image can never hold the door shut.
  const loader = document.getElementById('loader');
  if (loader) {
    let done = false;
    const dismiss = () => {
      if (done) return;
      done = true;
      loader.classList.add('out');
      setTimeout(() => loader.remove(), 520);
    };
    // The hold is set in Customize (Header › Homepage loader stays for):
    // the screen stays for that long, then goes - whether or not every
    // image has finished loading. Reduced-motion visitors see it briefly.
    const hold = reduceMotion ? 200 : (Number(loader.dataset.hold) || 3000);
    setTimeout(dismiss, hold);
  }

  // ── NAV SCROLL STATE ──────────────────────────────────────
  const mainNav = document.getElementById('mainNav');
  if (mainNav) {
    const paint = () => mainNav.classList.toggle('sc', window.scrollY > 60);
    onScroll(paint);
    paint();
  }

  // ── MOBILE NAV ────────────────────────────────────────────
  const mobBtn = document.getElementById('mobBtn');
  const mobNav = document.getElementById('mobNav');
  mobNavApi = null;
  if (mobBtn && mobNav) {
    let open = false;
    const set = (v) => {
      open = v;
      mobNav.classList.toggle('open', open);
      mobNav.setAttribute('aria-hidden', String(!open));
      mobBtn.setAttribute('aria-expanded', String(open));
      mobBtn.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
      document.body.style.overflow = open ? 'hidden' : '';
      const spans = mobBtn.querySelectorAll('span');
      if (open) {
        spans[0].style.transform = 'rotate(45deg) translate(5px, 5px)';
        spans[1].style.opacity = '0';
      } else {
        spans[0].style.transform = '';
        spans[1].style.opacity = '';
      }
    };
    mobBtn.addEventListener('click', () => set(!open));
    mobNav.querySelectorAll('a').forEach((a) => a.addEventListener('click', () => set(false)));
    mobNavApi = { isOpen: () => open, close: () => set(false) };
  }

  // ── HERO SLIDESHOW + STRIP INDICATOR ──────────────────────
  // A slide whose photo was removed in Customize renders empty (15 Sep
  // 2026): it and its dot sit out, so the hero never cycles to a blank
  // frame, and the dots only show for two photos or more.
  const allSlides = [...document.querySelectorAll('.h-slide')];
  const slideOk = allSlides.map((s) => !!s.querySelector('img'));
  const slides = allSlides.filter((s, i) => slideOk[i]);
  const hSlidesEl = document.getElementById('hSlides');
  const heroEvery = (hSlidesEl && Number(hSlidesEl.dataset.every)) || 6200;
  const allDots = [...document.querySelectorAll('#hDots .h-dot')];
  const hDots = allDots.filter((d, i) => slideOk[i]);
  const hScrollNum = document.querySelector('.h-scroll-num');
  if (slides.length < allSlides.length) {
    allSlides.forEach((s, i) => { if (!slideOk[i]) s.classList.remove('active'); });
    allDots.forEach((d, i) => { if (!slideOk[i]) { d.style.display = 'none'; d.classList.remove('active'); } });
    if (slides.length && !slides.some((s) => s.classList.contains('active'))) {
      slides[0].classList.add('active');
      if (hDots[0]) hDots[0].classList.add('active');
    }
    if (hScrollNum) {
      if (slides.length) hScrollNum.textContent = `01 / ${String(slides.length).padStart(2, '0')}`;
      else hScrollNum.style.display = 'none';
    }
  }
  const hDotsEl = document.getElementById('hDots');
  if (hDotsEl && slides.length < 2) hDotsEl.style.display = 'none';
  if (slides.length > 1) {
    let si = 0;
    const total = slides.length;
    const pad = (n) => String(n).padStart(2, '0');
    const update = () => {
      hDots.forEach((d, i) => d.classList.toggle('active', i === si));
      if (hScrollNum) hScrollNum.textContent = `${pad(si + 1)} / ${pad(total)}`;
    };
    const goTo = (i) => {
      slides[si].classList.remove('active');
      si = (i + total) % total;
      slides[si].classList.add('active');
      // restart the Ken Burns drift from scale(1) for the incoming slide
      const img = slides[si].querySelector('img');
      if (img && !reduceMotion) {
        img.style.animation = 'none';
        void img.offsetWidth;
        img.style.animation = '';
      }
      update();
    };
    update();
    let auto = null;
    if (!reduceMotion) {
      auto = setInterval(() => goTo(si + 1), heroEvery);
      pageIntervals.push(auto);
    }
    hDots.forEach((d, i) => d.addEventListener('click', () => {
      if (auto) { clearInterval(auto); auto = null; }
      goTo(i);
    }));
  }

  // ── REVEAL ON SCROLL ──────────────────────────────────────
  const revealSel = '.reveal,.reveal-x,.reveal-r,.reveal-wipe,.reveal-wipe-x';
  revealSweep = null;
  if (!reduceMotion && 'IntersectionObserver' in window) {
    const revealEls = [...document.querySelectorAll(revealSel)];
    const revealObs = onObserve(new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('vis'); revealObs.unobserve(e.target); }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -52px 0px' }));
    revealEls.forEach((el) => revealObs.observe(el));

    /* Backstop. .reveal-wipe hides content behind a clip-path, so a reveal
       that never fires leaves it invisible rather than merely un-animated —
       a decorative effect silently eating real content.

       That is reachable in normal use: a browser suspends
       IntersectionObserver while a tab is backgrounded, so a page opened
       with cmd-click, restored with a session, or throttled can finish
       loading having never received a callback. This recomputes the same
       rule directly from layout, which is cheap because it rides the
       existing rAF-throttled scroll driver and stops doing any work once
       everything has revealed. */
    /* Anything already in the viewport right now gets the quick entrance
       (.fastin) — the slow staggered pace is for elements arriving on
       scroll, not for the content the visitor is looking at. */
    const vh0 = window.innerHeight;
    revealEls.forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < vh0 && r.bottom > 0) el.classList.add('fastin');
    });

    let pending = revealEls;
    revealSweep = () => {
      if (!pending.length) return;
      const vh = window.innerHeight;
      pending = pending.filter((el) => {
        if (el.classList.contains('vis')) return false;
        const r = el.getBoundingClientRect();
        if (r.top < vh - 52 && r.bottom > 0) {
          el.classList.add('vis');
          revealObs.unobserve(el);
          return false;
        }
        return true;
      });
    };
    onScroll(revealSweep);
    revealSweep();

    /* The sweep above only sees the layout as it stands at wire-up time.
       Hero imagery, web fonts and the gallery grid all settle after that,
       and each reflow can push a new element into view without a scroll to
       announce it. The observer normally catches those; these re-checks
       mean a page that is never scrolled — someone landing deep-linked on
       a section, or reading without touching the wheel — cannot be left
       looking at a clipped, empty container. They only ever reveal what is
       genuinely in view, so nothing below the fold is spoiled. */
    [220, 800, 2000].forEach((ms) => setTimeout(revealSweep, ms));
    window.addEventListener('load', revealSweep, { once: true });
  } else {
    document.querySelectorAll(revealSel).forEach((el) => el.classList.add('vis'));
  }

  /* Theme editor. The customizer injects and re-renders sections without
     reloading, so they arrive after the observer was wired up and stay
     hidden (opacity 0 / clipped) until a refresh. Reveal whatever the
     editor adds or touches straight away. Never runs for visitors. */
  if (window.Shopify && window.Shopify.designMode) {
    const showAll = (root) => {
      if (!root || root.nodeType !== 1) return;
      if (root.matches(revealSel)) root.classList.add('vis', 'fastin');
      root.querySelectorAll(revealSel).forEach((el) => el.classList.add('vis', 'fastin'));
    };
    ['shopify:section:load', 'shopify:section:select', 'shopify:section:reorder', 'shopify:block:select']
      .forEach((ev) => document.addEventListener(ev, (e) => showAll(e.target)));

    /* The editor never re-runs a re-rendered section's own scripts either
       (shopify.dev: "any associated JavaScript that runs when the page loads
       won't run again"), so every carousel, film, gallery and the About strip
       froze after any setting change until a refresh (Naomi, 15 Sep 2026).
       Run a fresh copy of each inline script for the new markup; every
       section script guards its own elements, so nothing starts twice. */
    const rerun = (root) => {
      if (!root || root.nodeType !== 1) return;
      root.querySelectorAll('script:not([src])').forEach((old) => {
        if (old.type && !/^(text\/javascript|module)$/i.test(old.type)) return;
        const s = document.createElement('script');
        if (old.type) s.type = old.type;
        s.textContent = old.textContent;
        old.replaceWith(s);
      });
    };
    document.addEventListener('shopify:section:load', (e) => rerun(e.target));
    document.addEventListener('shopify:section:load', (e) => cloakedMailInit(e.target));
    new MutationObserver((muts) => muts.forEach((m) => m.addedNodes.forEach(showAll)))
      .observe(document.body, { childList: true, subtree: true });
  }

  // ── STATS COUNTERS ────────────────────────────────────────
  // Count-up animation removed intentionally (owner decision): the numbers
  // render static at their final values. The markup also ships the final
  // value server-side; this line just covers any stale cached HTML.
  document.querySelectorAll('.counter').forEach((el) => { el.textContent = el.dataset.target; });

  // ── PROCESS TIMELINE FILL ─────────────────────────────────
  const procTrack = document.querySelector('.proc-track');
  if (procTrack && !reduceMotion && 'IntersectionObserver' in window) {
    const procObs = onObserve(new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { procTrack.classList.add('fill'); procObs.unobserve(procTrack); } });
    }, { threshold: 0.3 }));
    procObs.observe(procTrack);
  } else if (procTrack) {
    procTrack.classList.add('fill');
  }

  // ── PARALLAX HERO CONTENT ─────────────────────────────────
  if (window.innerWidth > 768 && !reduceMotion) {
    const hContent = document.querySelector('.h-content');
    if (hContent) {
      onScroll(() => {
        if (window.scrollY < window.innerHeight) {
          hContent.style.transform = `translateY(${window.scrollY * 0.2}px)`;
        }
      });
    }
  }

  // ── MAGNETIC CTAs (subtle) ────────────────────────────────
  if (finePointer && !reduceMotion) {
    document.querySelectorAll('.btn-p, .btn-gold, .fsub').forEach((btn) => {
      btn.addEventListener('mousemove', (e) => {
        const r = btn.getBoundingClientRect();
        const x = e.clientX - r.left - r.width / 2;
        const y = e.clientY - r.top - r.height / 2;
        btn.style.transform = `translate(${x * 0.12}px, ${y * 0.18}px)`;
      });
      btn.addEventListener('mouseleave', () => { btn.style.transform = ''; });
    });
  }

  // ── 3D CARD TILT ──────────────────────────────────────────
  // Property cards lean gently toward the cursor. The .55s transform
  // transition on the card doubles as damping, so the tilt feels
  // weighted rather than twitchy.
  if (finePointer && !reduceMotion && window.innerWidth > 768) {
    document.querySelectorAll('.pc, .sc-card').forEach((card) => {
      card.addEventListener('mousemove', (e) => {
        const r = card.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        card.style.transform = `perspective(1100px) rotateX(${(-y * 3).toFixed(2)}deg) rotateY(${(x * 3).toFixed(2)}deg)`;
      });
      card.addEventListener('mouseleave', () => { card.style.transform = ''; });
    });
  }

  // ── CURSOR HOVER TARGETS ──────────────────────────────────
  if (finePointer && !reduceMotion && curDot) {
    const hov = 'a,button,.pc,.svc-item,.sc-card,.col-item,.proj-card,.about-val,.port-card,input,textarea,select';
    document.querySelectorAll(hov).forEach((el) => {
      el.addEventListener('mouseenter', () => document.body.classList.add('ch'));
      el.addEventListener('mouseleave', () => document.body.classList.remove('ch'));
    });
  }

  // ── CINEMATIC LAYER ───────────────────────────────────────
  // Scroll-linked: nav progress hairline, testimonial word illumination,
  // and parallax depth on editorial imagery. Transform/colour only,
  // skipped under prefers-reduced-motion.
  const progress = document.getElementById('navProgress');

  // Testimonial: split into word spans; words "light up" as the quote
  // moves through the viewport. Screen readers get the original text via
  // a visually-hidden copy (aria-label is prohibited on blockquote).
  let twSpans = [], twEl = null, twLit = -1;
  const quote = document.querySelector('.testi-text');
  if (quote && !reduceMotion && !quote.dataset.split) {
    quote.dataset.split = '1';
    const text = quote.textContent.trim();
    quote.textContent = '';
    const sr = document.createElement('span');
    sr.className = 'sr-only';
    sr.textContent = text;
    quote.appendChild(sr);
    text.split(/\s+/).forEach((w, i) => {
      if (i > 0) quote.appendChild(document.createTextNode(' '));
      const s = document.createElement('span');
      s.className = 'tw';
      s.setAttribute('aria-hidden', 'true');
      s.textContent = w;
      quote.appendChild(s);
    });
    twSpans = [...quote.querySelectorAll('.tw')];
    twEl = quote;
  }

  // Parallax: editorial images drift gently against scroll.
  const plxEls = (reduceMotion || window.innerWidth <= 768) ? [] :
    [...document.querySelectorAll('.page-hero img, .prop-hero img, .cd-hero img')];
  plxEls.forEach((el) => el.classList.add('plx'));

  if (progress || twEl || plxEls.length) {
    const cinematic = () => {
      const vh = window.innerHeight;

      if (progress) {
        const max = document.documentElement.scrollHeight - vh;
        progress.style.transform = `scaleX(${max > 0 ? Math.min(1, window.scrollY / max) : 0})`;
      }

      if (twEl) {
        const r = twEl.getBoundingClientRect();
        if (r.bottom > -100 && r.top < vh + 100) {
          const p = Math.min(1, Math.max(0, (vh * 0.82 - r.top) / (r.height + vh * 0.3)));
          const n = Math.round(p * twSpans.length);
          if (n !== twLit) {
            twSpans.forEach((s, i) => s.classList.toggle('lit', i < n));
            twLit = n;
          }
        }
      }

      plxEls.forEach((el) => {
        const box = el.parentElement.getBoundingClientRect();
        if (box.bottom < 0 || box.top > vh) return;
        const shift = ((box.top + box.height / 2) - vh / 2) * -0.07;
        el.style.transform = `translateY(${shift.toFixed(1)}px) scale(1.12)`;
      });
    };
    onScroll(cinematic);
    cinematic();
  }
}

/* astro:page-load fires on the initial load as well as after every
   client-side navigation, so this is the only entry point needed. */
document.addEventListener('astro:page-load', initPage);

/* ── Film bands ───────────────────────────────────────────────
   Port of FilmBand.astro's inline script — on Astro it ships with the
   component, so the theme has to carry it here. Drives every
   [data-film] frame: play once a third is visible, pause when fully
   out of view, retry transient play() refusals, and the sound toggle. */
function filmInit() {
  document.querySelectorAll('[data-film]').forEach(function (frame) {
    if (frame.dataset.filmInit) return;
    frame.dataset.filmInit = '1';
    var video = frame.querySelector('video');
    var btn = frame.querySelector('[data-sound]');
    var label = frame.querySelector('[data-sound-label]');
    var soundOn = label ? label.textContent : 'Sound On'; // the section's own Sound button word
    if (!video) return;

    if ('IntersectionObserver' in window) {
      /* play() is one-shot and its rejection used to be swallowed — a
         transient refusal (power saving, data not buffered, occluded
         tab) left the film permanently frozen until a manual refresh.
         The intent ("should be playing") is tracked and re-attempted
         on canplay, on tab visibility, and on a short bounded retry. */
      var wantPlay = false;
      var retries = 0;
      var attempt = function () {
        if (!wantPlay || !video.isConnected || !video.paused) return;
        var p = video.play();
        if (p && p.catch) p.catch(function () {
          if (wantPlay && retries++ < 12) setTimeout(attempt, 700);
        });
      };
      video.addEventListener('canplay', attempt);
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) attempt();
      });
      /* Hysteresis: start at a third visible, pause only once fully
         gone — a single threshold both ways rapid-cycles play/pause
         on every layout nudge: a visibly glitching film. */
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.intersectionRatio >= 0.35) {
            wantPlay = true;
            retries = 0;
            attempt();
          } else if (!en.isIntersecting) {
            wantPlay = false;
            video.pause();
          }
        });
      }, { threshold: [0, 0.35] });
      io.observe(frame);
    }

    if (btn && label) {
      btn.addEventListener('click', function () {
        video.muted = !video.muted;
        if (!video.muted && video.paused) video.play().catch(function () {});
        label.textContent = video.muted ? soundOn : WORD('mute', 'Mute');
      });
    }
  });
}

/* ── Wake films on touch (30 Sep 2026) ────────────────────────
   iPad/iPhone Safari refuses ALL autoplay in Low Power Mode (and some
   data-saving modes), even muted: the hero and every film band sat on
   their posters for good. A tap is a user gesture Safari always honours,
   so on each tap/click/key every film that should be running (autoplay
   or an auto [data-film] frame, on screen, paused) is started. Films
   that wait for a click (data-noauto) and Reduce Motion are left alone. */
function filmWakeInit() {
  if (window.__sabdiaFilmWake) return;
  window.__sabdiaFilmWake = true;
  var wake = function () {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    document.querySelectorAll('video').forEach(function (v) {
      if (!v.paused || !v.muted) return;
      var frame = v.closest('[data-film]');
      if (frame ? frame.dataset.noauto : !v.hasAttribute('autoplay')) return;
      var r = v.getBoundingClientRect();
      if (!r.width || !r.height || r.bottom <= 0 || r.top >= vh) return;
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    });
  };
  ['touchend', 'click', 'keydown'].forEach(function (ev) {
    document.addEventListener(ev, wake, { passive: true, capture: true });
  });
}

/* ── Shopify port ─────────────────────────────────────────────
   No Astro ClientRouter here, so astro:page-load never fires. Run the
   per-page init on plain document loads instead; the astro:* listeners
   above simply never fire and stay harmless. Lives INSIDE the module
   closure — initPage is not a global. */
function shopifyBoot() {
  initPage();
  filmInit();
  filmWakeInit();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', shopifyBoot);
else shopifyBoot();

})();

/* Journal edition galleries (23 Sep 2026): [residence: sierra] and
   [gallery: sierra] render a snapping stage with a strip of thumbnails.
   Delegated, so any number of galleries on one edition work, and a
   customizer edit that re-renders the page never leaves one dead. */
document.addEventListener('click', (e) => {
  const thumb = e.target.closest('.jm-thumb');
  if (!thumb) return;
  const gal = thumb.closest('[data-jm-gal]');
  const stage = gal && gal.querySelector('[data-jm-stage]');
  if (!stage) return;
  const i = parseInt(thumb.getAttribute('data-i') || '0', 10);
  stage.scrollTo({ left: stage.clientWidth * i, behavior: 'smooth' });
  gal.querySelectorAll('.jm-thumb').forEach((t) => t.classList.toggle('is-on', t === thumb));
});
document.addEventListener('scroll', (e) => {
  const stage = e.target.closest && e.target.closest('[data-jm-stage]');
  if (!stage) return;
  clearTimeout(stage._jmTimer);
  stage._jmTimer = setTimeout(() => {
    const gal = stage.closest('[data-jm-gal]');
    if (!gal) return;
    const i = Math.round(stage.scrollLeft / Math.max(stage.clientWidth, 1));
    gal.querySelectorAll('.jm-thumb').forEach((t, n) => {
      const on = n === i;
      t.classList.toggle('is-on', on);
      if (on) t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }, 90);
}, true);
/* Arrow keys move the stage when it has focus. */
document.addEventListener('keydown', (e) => {
  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
  const stage = document.activeElement && document.activeElement.closest('[data-jm-stage]');
  if (!stage) return;
  e.preventDefault();
  stage.scrollBy({ left: stage.clientWidth * (e.key === 'ArrowRight' ? 1 : -1), behavior: 'smooth' });
});
